import React, { useState, useCallback } from 'react';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import * as frac_api from "../../api/FracApi.ts";
import CircularProgress from '@mui/material/CircularProgress';
import { useDependencies } from '../../misc/DependenciesProvider.tsx';
import { useSnackbarMessage } from '../SnackMessge.tsx';
import { isUserAdmin } from '../../misc/UserFunctions.ts';
import { TextField, Box, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Select, MenuItem, Alert } from '@mui/material';
import * as api_gen from "../../api/General.ts";
import { DataTable } from '../DataTable.tsx';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// Green shades for the color picker
const GREEN_SHADES = [
    "#e0e0e0", "#e8f5e9", "#c8e6c9", "#a5d6a7", "#81c784",
    "#66bb6a", "#4caf50", "#43a047", "#388e3c", "#2e7d32", "#1b5e20"
];

interface FileUploadProps {
}

type UploadResultType = Blob | string | null | void;

export const FileUpload: React.FC<FileUploadProps> = () => {
    const { showMessage } = useSnackbarMessage();
    const { fracAPI } = useDependencies();
    const [uploadedFileHasErrors, setUploadedFileHasErrors] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadedOk, setUploadedOk] = useState<boolean>(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [downloadingTemplate, setDownloadingTemplate] = useState<boolean>(false);

    // Map data states
    const [mapDataYear, setMapDataYear] = useState<number>(new Date().getFullYear());
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [isCommitting, setIsCommitting] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const [isDownloadingData, setIsDownloadingData] = useState(false);
    const [isClearingData, setIsClearingData] = useState(false);

    // Bin configuration states
    const [isBinModalOpen, setIsBinModalOpen] = useState(false);
    const [densityBins, setDensityBins] = useState<frac_api.DensityBin[]>([]);
    const [isLoadingBins, setIsLoadingBins] = useState(false);
    const [binValidationError, setBinValidationError] = useState<string>("");

    // Computed: Check if any operation is in progress
    const isAnyOperationInProgress = isDownloadingTemplate || isLoadingPreview || isDownloadingData || isClearingData || isCommitting;

    const downloadFile = useCallback((blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }, []);

    const handleFileUploadResponse = useCallback((response: UploadResultType, selectedFile: File) => {
        if (response instanceof Blob) {
            downloadFile(response, selectedFile.name);
            setUploadedFileHasErrors(true);
            showMessage('Issues with file, please check the file.');
        } else if (typeof response === 'string') {
            showMessage(response);
            setFileError(response);
        } else {
            showMessage('File uploaded successfully!');
            setUploadedOk(true);
        }
    }, [downloadFile, showMessage]);


    const handleUpload = useCallback(async (selectedFile: File) => {
        setUploadedFileHasErrors(false);
        setUploading(true);
        setUploadedOk(false);
        setFileError(null);

        try {
            const blobOrError: UploadResultType = await fracAPI.uploadFile(selectedFile);
            handleFileUploadResponse(blobOrError, selectedFile);
        } catch (error: any) {
            console.error('Error uploading file:', error);
            showMessage('Failed to upload file.');
        } finally {
            setUploading(false);
        }
    }, [fracAPI, handleFileUploadResponse, showMessage]);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const selectedFile = files[0];
            handleUpload(selectedFile);
        }
        // ✅ STANDARD FIX: Clear the input immediately so the next change registers
        event.target.value = '';
    }, [handleUpload]);


    const handleTemplateDownloadResponse = useCallback((response: UploadResultType) => {
        if (response instanceof Blob) {
            downloadFile(response, 'template.xlsx');
        }
    }, [downloadFile]);

    const downloadTemplate = useCallback(async () => {
        setDownloadingTemplate(true);
        try {
            const blobOrError: UploadResultType = await fracAPI.getTemplate();
            handleTemplateDownloadResponse(blobOrError);
        } finally {
            setDownloadingTemplate(false);
        }
    }, [fracAPI, handleTemplateDownloadResponse]);

    const downloadMapDataTemplate = async () => {
        setIsDownloadingTemplate(true);
        try {
            const blob = await api_gen.getFile(`download_map_template?year=${mapDataYear}`);
            if (blob) downloadFile(blob, `map_data_points_template_${mapDataYear}.xlsx`);
        } catch (e: any) {
            showMessage("Error downloading template");
        } finally {
            setIsDownloadingTemplate(false);
        }
    };

    const handleMultiFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (files && files.length > 0) {
            const filesArray = Array.from(files);

            // ✅ STANDARD FIX: Clear the input immediately so the next change registers
            event.target.value = '';

            setIsLoadingPreview(true);
            try {
                const result = await fracAPI.previewMapData(filesArray, mapDataYear);
                setPreviewData(result);
                if (result.length === 0) {
                    showMessage("No valid data found in uploaded files. Please ensure 'Data Points' column has numeric values.");
                }
            } catch (e: any) {
                const errorMessage = e.message || "Error generating preview.";
                showMessage(errorMessage);
                console.error("Preview error:", e);
            } finally {
                setIsLoadingPreview(false);
            }
        }
    };

    const commitMapData = async () => {
        setIsCommitting(true);
        try {
            const response = await fracAPI.commitMapData(previewData);
            showMessage(response.message);
            setPreviewData([]); // Clear preview on success
        } catch (e) {
            showMessage("Error saving map data.");
        } finally {
            setIsCommitting(false);
        }
    };

    const downloadMapDataForYear = async () => {
        setIsDownloadingData(true);
        try {
            const blob = await fracAPI.downloadMapData(mapDataYear);
            if (blob) {
                downloadFile(blob, `map_data_points_${mapDataYear}.xlsx`);
                showMessage(`Downloaded map data for year ${mapDataYear}`);
            }
        } catch (e: any) {
            const errorMessage = e.message || `No data found for year ${mapDataYear}`;
            showMessage(errorMessage);
        } finally {
            setIsDownloadingData(false);
        }
    };

    const clearMapDataForYear = async () => {
        if (!window.confirm(`Are you sure you want to delete ALL map data for year ${mapDataYear}? This action cannot be undone.`)) {
            return;
        }

        setIsClearingData(true);
        try {
            const response = await fracAPI.clearMapData(mapDataYear);
            showMessage(response.message);
        } catch (e: any) {
            const errorMessage = e.message || `Error clearing data for year ${mapDataYear}`;
            showMessage(errorMessage);
        } finally {
            setIsClearingData(false);
        }
    };

    const openBinModal = async () => {
        setIsLoadingBins(true);
        setIsBinModalOpen(true);
        setBinValidationError("");
        try {
            const bins = await fracAPI.getMapDensityBins(mapDataYear);
            setDensityBins(bins);
        } catch (e) {
            showMessage("Failed to load bin configuration.");
        } finally {
            setIsLoadingBins(false);
        }
    };

    const validateBins = (bins: frac_api.DensityBin[]): { isValid: boolean; error: string } => {
        if (bins.length === 0) {
            return { isValid: false, error: "At least one bin is required" };
        }

        // Sort bins by min_val
        const sortedBins = [...bins].sort((a, b) => a.min_val - b.min_val);

        // Check that the first bin starts at 0
        if (sortedBins[0].min_val !== 0) {
            return {
                isValid: false,
                error: `First bin must start at 0, but starts at ${sortedBins[0].min_val}`
            };
        }

        // Check that each bin has min <= max (except last bin which is auto-set to 999999)
        for (let i = 0; i < sortedBins.length - 1; i++) {
            const bin = sortedBins[i];

            if (bin.min_val > bin.max_val) {
                return {
                    isValid: false,
                    error: `Bin '${bin.label}' has min value (${bin.min_val}) greater than max value (${bin.max_val})`
                };
            }
        }

        // Note: With the new UI, gaps and overlaps are automatically prevented
        // by the cascading updates, so we don't need to check for them

        return { isValid: true, error: "" };
    };

    const saveBins = async () => {
        // Ensure the last bin has max_val set to 999999
        const binsToSave = densityBins.map((bin, idx) => {
            if (idx === densityBins.length - 1) {
                return { ...bin, max_val: 999999 };
            }
            return bin;
        });

        // Validate before saving
        const validation = validateBins(binsToSave);
        if (!validation.isValid) {
            showMessage(validation.error);
            return;
        }

        try {
            await fracAPI.saveMapDensityBins(mapDataYear, binsToSave);
            showMessage("Bin configuration saved successfully.");
            setIsBinModalOpen(false);
        } catch (e: any) {
            const errorMessage = e.message || "Failed to save bin configuration.";
            showMessage(errorMessage);
        }
    };

    const updateBin = (index: number, field: keyof frac_api.DensityBin, value: any) => {
        const updated = [...densityBins];
        updated[index] = { ...updated[index], [field]: value };

        // If max_val changed, cascade the update to subsequent bins
        if (field === 'max_val' && index < updated.length - 1) {
            const newMax = Number(value);
            // Update the min_val of the next bin to be newMax + 1
            for (let i = index + 1; i < updated.length; i++) {
                updated[i] = { ...updated[i], min_val: updated[i - 1].max_val + 1 };
            }
        }

        // If first bin's min_val changed, ensure it stays at 0 or cascade from there
        if (field === 'min_val' && index === 0) {
            // First bin min should always be 0, but if user changes it we need to cascade
            const newMin = Number(value);
            if (newMin !== 0) {
                // Force it back to 0
                updated[0] = { ...updated[0], min_val: 0 };
            }
        }

        setDensityBins(updated);

        // Ensure last bin always has max_val set to 999999 for validation
        const binsForValidation = updated.map((bin, idx) => {
            if (idx === updated.length - 1) {
                return { ...bin, max_val: 999999 };
            }
            return bin;
        });

        // Check validation and update error state
        const validation = validateBins(binsForValidation);
        setBinValidationError(validation.isValid ? "" : validation.error);
    };

    const insertBinAfter = (index: number) => {
        if (densityBins.length >= 10) {
            showMessage("Maximum of 10 bins allowed.");
            return;
        }

        const bins = [...densityBins];
        const afterBin = bins[index];

        // New bin starts right after the current bin with minimal range
        const newBin = {
            year: mapDataYear,
            min_val: afterBin.max_val + 1,
            max_val: afterBin.max_val + 1,  // Single value bin (min range)
            color: GREEN_SHADES[5],
            label: "New Bin"
        };

        // Insert after the current index
        bins.splice(index + 1, 0, newBin);

        // Cascade updates to all following bins
        for (let i = index + 2; i < bins.length; i++) {
            bins[i] = { ...bins[i], min_val: bins[i - 1].max_val + 1 };
        }

        setDensityBins(bins);

        // Check validation with last bin set to 999999
        const binsForValidation = bins.map((bin, idx) => {
            if (idx === bins.length - 1) {
                return { ...bin, max_val: 999999 };
            }
            return bin;
        });
        const validation = validateBins(binsForValidation);
        setBinValidationError(validation.isValid ? "" : validation.error);
    };

    const removeBin = (index: number) => {
        if (densityBins.length === 1) {
            showMessage("At least one bin is required.");
            return;
        }

        // Remove the bin at the specified index
        let newBins = densityBins.filter((_, i) => i !== index);

        // If we removed the first bin, ensure the new first bin starts at 0
        if (index === 0) {
            newBins[0] = { ...newBins[0], min_val: 0 };
            // Recalculate min values for all subsequent bins
            for (let i = 1; i < newBins.length; i++) {
                newBins[i] = { ...newBins[i], min_val: newBins[i - 1].max_val + 1 };
            }
        }
        // If we removed a middle bin, recalculate min values for all bins after the deletion point
        else if (index > 0) {
            for (let i = index; i < newBins.length; i++) {
                newBins[i] = { ...newBins[i], min_val: newBins[i - 1].max_val + 1 };
            }
        }

        setDensityBins(newBins);

        // Check validation with last bin set to 999999
        const binsForValidation = newBins.map((bin, idx) => {
            if (idx === newBins.length - 1) {
                return { ...bin, max_val: 999999 };
            }
            return bin;
        });
        const validation = validateBins(binsForValidation);
        setBinValidationError(validation.isValid ? "" : validation.error);
    };

    // Preview Columns for DataTable
    const previewCols = [
        { id: 'Country', disp_name: 'Country', type: 'str', rows: 1, wrap: false, hidden: false, editable: false },
        { id: 'Pathogen (Pest Scientific Name)', disp_name: 'Pathogen', type: 'str', rows: 1, wrap: false, hidden: false, editable: false },
        { id: 'Crop', disp_name: 'Crop', type: 'str', rows: 1, wrap: false, hidden: false, editable: false },
        { id: 'FRAC MoA Code', disp_name: 'MoA Code', type: 'str', rows: 1, wrap: false, hidden: false, editable: false },
        { id: 'Year', disp_name: 'Year', type: 'str', rows: 1, wrap: false, hidden: false, editable: false },
        { id: 'Data Points', disp_name: 'Total Data Points', type: 'number', rows: 1, wrap: false, hidden: false, editable: false },
    ];

    // ✅ Render body is now clean, stable, and flat. No nested components.
    return (
        <div>
            Upload resistance monitoring data file.
            <br />

            {/* Template Download Section Inlined */}
            {!downloadingTemplate ? (
                <>
                    Please <a href='#' onClick={(e) => { e.preventDefault(); downloadTemplate(); }}>download</a> the template.
                </>
            ) : (
                <>
                    Please download <CircularProgress size={14} /> the template. (Generating template, please wait).
                </>
            )}

            <br /><br />

            <Button
                component="label"
                role={undefined}
                disabled={uploading}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
            >
                Select and Upload file
                <VisuallyHiddenInput
                    id="file-upload-input"
                    type="file"
                    onChange={handleFileChange}
                />
                {uploading && <CircularProgress size={24} />}
            </Button>

            <br /><br />

            {/* Upload Status Section Inlined */}
            {uploadedFileHasErrors && (
                <div>
                    <b>There were issues with the data in the file.</b>
                    <br />
                    The specific errors have been added to the file and should be downloding now.
                    <br />
                    Please correct the issues and try again.
                </div>
            )}

            {uploadedOk && (
                <div>
                    <b>File uploaded successfully</b>
                </div>
            )}

            {fileError && (
                <div>
                    <b>Problem with file:
                        <li>{fileError}</li>
                    </b>
                </div>
            )}

            {/* Admin Section Inlined */}
            {isUserAdmin() && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6">Global Map Data Points (Admin Only)</Typography>
                    <Typography variant="body2" gutterBottom component="div">
                        <ul>
                            <li>Select a year and download the template. </li>
                            <li>Distribute. </li>
                            <li>Upload all completed files for that year. The systen will sum up the data point counts, and show you data, then you can upload it.</li>
                        </ul>
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>
                        <TextField
                            label="Year"
                            type="number"
                            value={mapDataYear}
                            onChange={(e) => setMapDataYear(Number(e.target.value))}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>
                        <Button
                            variant="contained"
                            onClick={downloadMapDataTemplate}
                            startIcon={<DownloadIcon />}
                            disabled={isAnyOperationInProgress}
                        >
                            Download Template
                            {isDownloadingTemplate && <CircularProgress size={24} sx={{ ml: 1 }} />}
                        </Button>

                        <Button
                            component="label"
                            variant="contained"
                            color="secondary"
                            startIcon={<CloudUploadIcon />}
                            disabled={isAnyOperationInProgress}
                        >
                            Upload and validate completed templates...
                            <VisuallyHiddenInput
                                type="file"
                                multiple
                                accept=".xlsx"
                                onChange={handleMultiFileSelect}
                            />
                            {isLoadingPreview && <CircularProgress size={24} sx={{ ml: 1 }} />}
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>

                        <Button
                            variant="outlined"
                            color="info"
                            onClick={downloadMapDataForYear}
                            startIcon={<DownloadIcon />}
                            disabled={isAnyOperationInProgress}
                        >
                            Download Existing Data
                            {isDownloadingData && <CircularProgress size={24} sx={{ ml: 1 }} />}
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            onClick={clearMapDataForYear}
                            disabled={isAnyOperationInProgress}
                        >
                            Clear All Data for Year
                            {isClearingData && <CircularProgress size={24} sx={{ ml: 1 }} />}
                        </Button>

                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={openBinModal}
                            disabled={isAnyOperationInProgress}
                        >
                            Set bin colours for year...
                        </Button>
                    </Box>

                    {previewData.length > 0 && (
                        <Box sx={{ mt: 4, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
                            <Typography variant="h6" color="primary">Preview Merged Data</Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Review the summed totals. Click Confirm to save to the database.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={commitMapData}
                                    disabled={isAnyOperationInProgress}
                                >
                                    Confirm & Save Data
                                    {isCommitting && <CircularProgress size={24} sx={{ ml: 1 }} />}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setPreviewData([])}
                                    disabled={isAnyOperationInProgress}
                                >
                                    Cancel
                                </Button>
                            </Box>

                            <DataTable
                                showExport={false}
                                keyname="Country"
                                data={previewData}
                                columns={previewCols}
                                defaultSortColumn="Country"
                                getTokenFuncAsync={async () => null}
                            />
                        </Box>
                    )}

                    {/* Bin Configuration Modal */}
                    <Dialog open={isBinModalOpen} onClose={() => setIsBinModalOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle>Configure Density Map Bins for {mapDataYear}</DialogTitle>
                        <DialogContent>
                            {isLoadingBins ? <CircularProgress /> : (
                                <Box sx={{ mt: 2 }}>
                                    {binValidationError && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {binValidationError}
                                        </Alert>
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        <strong>Rules:</strong> The first bin starts at 0. Each bin's max value automatically sets the next bin's min value. The last bin automatically covers all values to infinity.
                                    </Typography>
                                    {densityBins.map((bin, idx) => {
                                        const isFirstBin = idx === 0;
                                        const isLastBin = idx === densityBins.length - 1;
                                        return (
                                            <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                                <TextField
                                                    label="Min"
                                                    type="number"
                                                    size="small"
                                                    sx={{ width: 100 }}
                                                    value={bin.min_val}
                                                    onChange={(e) => isFirstBin && updateBin(idx, 'min_val', Number(e.target.value))}
                                                    disabled={!isFirstBin}
                                                    InputProps={{
                                                        readOnly: !isFirstBin,
                                                    }}
                                                />
                                                <TextField
                                                    label="Max"
                                                    type={isLastBin ? "text" : "number"}
                                                    size="small"
                                                    sx={{ width: 200 }}
                                                    value={isLastBin ? "Max" : bin.max_val}
                                                    onChange={(e) => !isLastBin && updateBin(idx, 'max_val', Number(e.target.value))}
                                                    disabled={isLastBin}
                                                    InputProps={{
                                                        readOnly: isLastBin,
                                                        inputProps: !isLastBin ? {
                                                            min: bin.min_val
                                                        } : undefined
                                                    }}
                                                />
                                                <TextField
                                                    label="Label"
                                                    size="small"
                                                    sx={{ flexGrow: 1 }}
                                                    value={bin.label}
                                                    onChange={(e) => updateBin(idx, 'label', e.target.value)}
                                                />
                                                <Select
                                                    size="small"
                                                    value={bin.color}
                                                    onChange={(e) => updateBin(idx, 'color', e.target.value)}
                                                    sx={{ width: 120, bgcolor: bin.color }}
                                                >
                                                    {GREEN_SHADES.map(color => (
                                                        <MenuItem
                                                            key={color}
                                                            value={color}
                                                            sx={{
                                                                bgcolor: color,
                                                                height: 30,
                                                                '&:hover': { opacity: 0.8 }
                                                            }}
                                                        >
                                                            &nbsp;
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {!isLastBin ? (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => insertBinAfter(idx)}
                                                        disabled={densityBins.length >= 10}
                                                        title="Insert bin after this one"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                ) : (
                                                    <Box sx={{ width: 40 }} />
                                                )}
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeBin(idx)}
                                                    disabled={densityBins.length === 1}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsBinModalOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={saveBins}
                                disabled={isLoadingBins || !!binValidationError}
                            >
                                Save Bins
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </div >
    );
};